import React, { useRef, useState } from 'react';
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useDndMonitor,
	useSensor,
	useSensors,
	type DragEndEvent,
	type UniqueIdentifier,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// A press that moves less than this is a click, so a handle can also open its item
const DRAG_DISTANCE = 4;

type SortableListProps = {
	/**
	 * One per item, in their current order. An id changes with its item's content (e.g. `${index}:${value}`): the list
	 * shows a drop's order until the moved data arrives as new ids.
	 */
	ids: UniqueIdentifier[];
	/** Called once on drop; items only shift visually while dragging */
	onMove: (from: number, to: number) => void;
	/** SortableList.Item elements */
	children: React.ReactNode;
};

const isSameOrder = (a: UniqueIdentifier[], b: UniqueIdentifier[]) =>
	a.length === b.length && a.every((id, index) => id === b[index]);

/**
 * A vertical list reordered by dragging an item's handle, or with the keyboard: Space on a handle, then the arrows.
 * Other items move aside while dragging; the new order is reported once, on drop.
 */
function SortableList({ ids, onMove, children }: SortableListProps) {
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: DRAG_DISTANCE } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	);
	// The host saves a move asynchronously; until its ids change, the list keeps the dropped order instead of
	// snapping back to the old one for a moment
	const [dropped, setDropped] = useState<{ ids: UniqueIdentifier[]; order: UniqueIdentifier[] } | null>(null);
	const order = dropped && isSameOrder(dropped.ids, ids) ? dropped.order : ids;
	// Each place in the list keeps one row element; a drop moves those keys with its items, so the row under the
	// pointer stays the same element when the moved data arrives and its hover doesn't flicker
	const [slots, setSlots] = useState(() => ({ keys: ids.map((_, index) => index), next: ids.length }));
	let { keys } = slots;
	if (keys.length !== ids.length) {
		keys = ids.map((_, index) => slots.next + index);
		setSlots({ keys, next: slots.next + ids.length });
	}
	const items = new Map(
		React.Children.toArray(children)
			.filter(React.isValidElement<SortableItemProps>)
			.map(child => [child.props.id, child])
	);

	const onDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return;
		const from = ids.indexOf(active.id);
		const to = ids.indexOf(over.id);
		setDropped({ ids, order: arrayMove(ids, from, to) });
		setSlots(previous => ({ ...previous, keys: arrayMove(previous.keys, from, to) }));
		onMove(from, to);
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			modifiers={[restrictToVerticalAxis]}
			onDragStart={() => setDropped(null)}
			onDragEnd={onDragEnd}
		>
			<SortableContext items={order} strategy={verticalListSortingStrategy}>
				{order.map((id, index) => {
					const item = items.get(id);
					return item ? React.cloneElement(item, { key: keys[index] }) : null;
				})}
			</SortableContext>
		</DndContext>
	);
}

export type SortableItemState = {
	/** The moving element: the item's own root */
	ref: (element: HTMLElement | null) => void;
	style: React.CSSProperties;
	/** Spread onto the element that starts a drag, e.g. a grip or a preview */
	handle: React.HTMLAttributes<HTMLElement> & { ref: (element: HTMLElement | null) => void };
	isDragging: boolean;
};

type SortableItemProps = {
	id: UniqueIdentifier;
	disabled?: boolean;
	children: (state: SortableItemState) => React.ReactNode;
};

/** One item of a SortableList; it places the ref, style and handle on its own markup */
function SortableItem({ id, disabled, children }: SortableItemProps) {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
		id,
		disabled,
	});
	// A drop ends with a click on the handle; it must not reach the item's own click, such as opening an editor
	const draggedRef = useRef(false);
	const release = () => {
		draggedRef.current = false;
	};
	useDndMonitor({
		onDragStart: event => {
			if (event.active.id === id) draggedRef.current = true;
		},
		onDragEnd: () => setTimeout(release),
		onDragCancel: () => setTimeout(release),
	});

	return (
		<>
			{children({
				ref: setNodeRef,
				style: { transform: CSS.Translate.toString(transform), transition },
				handle: {
					...attributes,
					...listeners,
					ref: setActivatorNodeRef,
					onClickCapture: event => {
						if (!draggedRef.current) return;
						event.preventDefault();
						event.stopPropagation();
					},
				},
				isDragging,
			})}
		</>
	);
}

export default Object.assign(SortableList, { Item: SortableItem });
